import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Search, FileText, ShieldAlert } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import JeaButton from "@/components/jea/JeaButton";
import { demoClient, useDemo } from "@/pages/demo/DemoShell";
import { formatMoney } from "@/lib/pricing";

const StatCard = ({ label, value, Icon }) => (
  <JeaCard>
    <div className="flex items-center justify-between">
      <div className="jea-caption">{label}</div>
      {Icon && <Icon size={16} className="text-text-muted" />}
    </div>
    <div className="jea-mono text-white font-extrabold text-[28px] mt-2">{value}</div>
  </JeaCard>
);

const DemoDashboardPage = () => {
  const [state, setState] = useState({ debtors: [], stats: null, loading: true });
  const demo = useDemo();

  useEffect(() => {
    (async () => {
      try {
        const [dRes, sRes] = await Promise.all([
          demoClient.get("/demo/debtors"),
          demoClient.get("/demo/queries/stats"),
        ]);
        setState({ debtors: dRes.data.debtors || [], stats: sRes.data, loading: false });
      } catch { setState((s) => ({ ...s, loading: false })); }
    })();
  }, []);

  const totalValue = state.debtors.reduce((acc, d) => acc + (d.judgment?.amount_cents || 0), 0);
  const nudge = () => demo?.openSignUp("running queries");

  return (
    <div className="space-y-6" data-testid="demo-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="space-y-1">
          <div className="jea-caption">Command Center</div>
          <h1 className="text-[28px] font-bold text-white">PRO Dashboard</h1>
        </div>
        <JeaButton onClick={nudge} data-testid="demo-dashboard-add-debtor">+ Add New Judgment</JeaButton>
      </div>

      {state.loading ? (
        <Skeleton className="h-24" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Judgments" value={state.debtors.length} Icon={Users} />
          <StatCard label="Queries" value={state.stats?.total_runs ?? 0} Icon={Search} />
          <StatCard label="Reports" value={state.stats?.reports_total ?? 0} Icon={FileText} />
          <StatCard label="Portfolio Value" value={formatMoney(totalValue)} Icon={ShieldAlert} />
        </div>
      )}

      <JeaCard data-testid="demo-dashboard-debtors">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-bold text-[16px]">Judgment Portfolio</h2>
          <Link to="/demo/debtors" className="text-accent-pink text-[13px] font-semibold hover:text-accent-pink-light">
            View all →
          </Link>
        </div>
        {state.loading ? <Skeleton className="h-32" /> : (
          <div className="space-y-2">
            {state.debtors.slice(0, 5).map((d) => (
              <Link
                key={d.id}
                to={`/demo/debtors/${d.id}`}
                className="flex items-center gap-4 bg-bg-primary border border-border-default rounded-[8px] p-4 hover:border-accent-pink/40 transition-colors"
                data-testid={`demo-debtor-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold text-[14px]">{d.full_name}</div>
                  <div className="text-text-secondary text-[12px] jea-mono">{d.judgment?.case_number || "—"}</div>
                </div>
                <div className="jea-mono text-accent-pink font-bold text-[15px]">
                  {formatMoney(d.judgment?.amount_cents || 0)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </JeaCard>
    </div>
  );
};

export default DemoDashboardPage;
